import _ from 'underscore'
import React from 'react'
import styled from 'styled-components'

import LegendGroupItem from 'pages/Device/ThermostatRthPanel/Legend/LegendGroupItem'
import LegendGroupSelection from 'pages/Device/ThermostatRthPanel/Legend/LegendGroupSection'

import { AlertsLegendItem, LegendItem, legendZone, legendDataForTimeAtCursor } from 'types/RuntimeHistory'

import { amps, degreesFahrenheit, rpsToRpm, rpm, rpmToPwm, voltageAC, watts } from 'helpers/formatters'
import GA4 from 'helpers/ga4'

type Props = {
  noDataText?:string
  label: string
  legendItems: Array<LegendItem> | ReadonlyArray<AlertsLegendItem>
  zones?: Array<legendZone>
  isControlHub?: boolean
  legendDataForTimeAtCursor?: Required<legendDataForTimeAtCursor> | null
  selectedLegends: Array<string>
  onLegendItemClickCallback?: (seriesName) => void
  onLegendItemSelectAllClickCallback: (seriesName, isSelected) => void
  showCompressorSpeedInOutdoor?: boolean
}

const LegendGroup = ({ noDataText, legendDataForTimeAtCursor, label, selectedLegends, isControlHub, legendItems, onLegendItemSelectAllClickCallback, onLegendItemClickCallback = () => {}, showCompressorSpeedInOutdoor = true }: Props) => {
  const allSeriesNames = _.pluck(legendItems, 'seriesName')
  const seriesWithData = _.pluck(_.filter(legendItems, (legendItem) => !legendItem.emptyDataSet), 'seriesName')
  const areAllSeriesSelected = _.without(seriesWithData, ...selectedLegends).length === 0 // set true if all series with data selected
  const areNoSeriesSelected = _.without(seriesWithData, ...selectedLegends).length === seriesWithData.length // set true if all series with data not selected

  const onClickCheckmark = (seriesName) => {
    sendGA4event(selectedLegends.includes(seriesName) ? 'Deselect a legend item for the graph' : 'Select a legend item for the graph', seriesName)
    onLegendItemClickCallback(seriesName)
  }

  const onClickSelectAll = (selectAll) => {
    sendGA4event(selectAll ? 'Selected All metrics in a group' : 'Select No metrics in a group', label)
    if (selectAll) {
      onLegendItemSelectAllClickCallback(allSeriesNames, true)
    } else {
      onLegendItemSelectAllClickCallback(allSeriesNames, false)
    }
  }

  const sendGA4event = (actionText, labelText) => {
    GA4.sendEvent({ category: 'Customer Detail Page', action: actionText, label: labelText })
  }

  return (
    <S.LegendGroup data-testid={label}>
      <LegendGroupSelection areAllSeriesSelected={areAllSeriesSelected} areNoSeriesSelected={areNoSeriesSelected} onClickSelectAllCallback={onClickSelectAll} label={label}>
        {legendItems.map((item) => (
          <LegendGroupItem
              onClickCheckmarkCallback={onClickCheckmark}
              noDataText={noDataText}
              legendItem={item}
              selectedLegendItems={selectedLegends}
              legendDataForTimeAtCursor={legendDataForTimeAtCursor}
              isSelected={selectedLegends.includes(item.seriesName)}
              key={item.seriesName || item.label}
          />
        )
        )}
      </LegendGroupSelection>
      {legendDataForTimeAtCursor && label === 'Indoor Unit' &&
        <>
          <S.AnscillaryContainer><S.AnscillaryLabel>Target Airflow</S.AnscillaryLabel> <S.AnscillaryData>{legendDataForTimeAtCursor.targetAirflow || '--'}</S.AnscillaryData></S.AnscillaryContainer>
          <S.AnscillaryContainer><S.AnscillaryLabel>Total Static Pressure</S.AnscillaryLabel><S.AnscillaryData>{legendDataForTimeAtCursor.staticPressure || '--'}</S.AnscillaryData></S.AnscillaryContainer>
          {isControlHub &&
            <>
              <S.AnscillaryContainer><S.AnscillaryLabel>Return Static </S.AnscillaryLabel><S.AnscillaryData>{legendDataForTimeAtCursor.returnStaticPressure || '--'}</S.AnscillaryData></S.AnscillaryContainer>
              <S.AnscillaryContainer><S.AnscillaryLabel>Supply Static</S.AnscillaryLabel> <S.AnscillaryData>{legendDataForTimeAtCursor.supplyStaticPressure || '--'}</S.AnscillaryData></S.AnscillaryContainer>
              {legendDataForTimeAtCursor.isSpeedInducer && <S.AnscillaryContainer><S.AnscillaryLabel>Actual Inducer Speed</S.AnscillaryLabel><S.AnscillaryData>{legendDataForTimeAtCursor.isSpeedInducer}</S.AnscillaryData></S.AnscillaryContainer>}
              {legendDataForTimeAtCursor.idCombustionPressureActual && <S.AnscillaryContainer><S.AnscillaryLabel>Actual Combustion Pressure</S.AnscillaryLabel> <S.AnscillaryData>{legendDataForTimeAtCursor.idCombustionPressureActual}</S.AnscillaryData></S.AnscillaryContainer>}
              {legendDataForTimeAtCursor.idCombustionPressureTarget && <S.AnscillaryContainer><S.AnscillaryLabel>Target Combustion Pressure</S.AnscillaryLabel><S.AnscillaryData>{legendDataForTimeAtCursor.idCombustionPressureTarget}</S.AnscillaryData></S.AnscillaryContainer>}
              {legendDataForTimeAtCursor.idGasValvePositionActual && <S.AnscillaryContainer><S.AnscillaryLabel>Actual Gas Valve Stepper Position</S.AnscillaryLabel> <S.AnscillaryData>{legendDataForTimeAtCursor.idGasValvePositionActual}</S.AnscillaryData></S.AnscillaryContainer>}
              {legendDataForTimeAtCursor.idGasValvePositionSetpoint && <S.AnscillaryContainer><S.AnscillaryLabel>Gas Valve Setpoint</S.AnscillaryLabel><S.AnscillaryData>{legendDataForTimeAtCursor.idGasValvePositionSetpoint}</S.AnscillaryData></S.AnscillaryContainer>}
              {legendDataForTimeAtCursor.idTemperatureRise && <S.AnscillaryContainer><S.AnscillaryLabel>Temperature Rise - Measured</S.AnscillaryLabel><S.AnscillaryData>{legendDataForTimeAtCursor.idTemperatureRise}</S.AnscillaryData></S.AnscillaryContainer>}
              {legendDataForTimeAtCursor.idTemperatureRiseTarget && <S.AnscillaryContainer><S.AnscillaryLabel>Target Temperature Rise</S.AnscillaryLabel> <S.AnscillaryData>{legendDataForTimeAtCursor.idTemperatureRiseTarget}</S.AnscillaryData></S.AnscillaryContainer>}
              {legendDataForTimeAtCursor.idLineVoltage && <S.AnscillaryContainer><S.AnscillaryLabel>Line Voltage</S.AnscillaryLabel><S.AnscillaryData>{legendDataForTimeAtCursor.idLineVoltage}</S.AnscillaryData></S.AnscillaryContainer>}
              {legendDataForTimeAtCursor.requestedModulation && <S.AnscillaryContainer><S.AnscillaryLabel>Target Demand</S.AnscillaryLabel> <S.AnscillaryData>{legendDataForTimeAtCursor.requestedModulation}</S.AnscillaryData></S.AnscillaryContainer>}
            </>
          }

        </>
      }

      {showCompressorSpeedInOutdoor && legendDataForTimeAtCursor && label === 'Outdoor' &&
      <S.AnscillaryContainer>
        <S.AnscillaryLabel>Compressor Speed</S.AnscillaryLabel>
        <S.AnscillaryData>{legendDataForTimeAtCursor.outdoorCapacity || '--'}</S.AnscillaryData>
      </S.AnscillaryContainer>
      }

      {legendDataForTimeAtCursor && label === 'Outdoor Unit' &&
        <>
          <S.AnscillaryContainer>
            <S.AnscillaryLabel>Compressor Speed</S.AnscillaryLabel>
            <S.AnscillaryData>
              <span>{legendDataForTimeAtCursor.outdoorCapacity || '--'}</span>
              <span>{rpsToRpm(legendDataForTimeAtCursor.odCompressorActualSpeed) || '--'}</span>
            </S.AnscillaryData>
          </S.AnscillaryContainer>
          {legendDataForTimeAtCursor.odDriveType && legendDataForTimeAtCursor.odDriveType !== 'UNKNOWN' && (
            <>
              {legendDataForTimeAtCursor.odDriveType === 'DANFOSS' &&
              <S.AnscillaryContainer>
                <S.AnscillaryLabel>OD Fan PWM</S.AnscillaryLabel>
                <S.AnscillaryData>{rpmToPwm(legendDataForTimeAtCursor.odFanSpeed) || '--'}</S.AnscillaryData>
              </S.AnscillaryContainer>
              }

              { (legendDataForTimeAtCursor.odDriveType === 'SINRAPTOR' || legendDataForTimeAtCursor.odDriveType === 'RUBICON') &&
              <S.AnscillaryContainer>
                <S.AnscillaryLabel>OD Fan RPM</S.AnscillaryLabel>
                <S.AnscillaryData>{rpm(legendDataForTimeAtCursor.odFanSpeed) || '--'}</S.AnscillaryData>
              </S.AnscillaryContainer>
              }
              <S.AnscillaryContainer>
                <S.AnscillaryLabel>OD Fan Power</S.AnscillaryLabel>
                <S.AnscillaryData>{watts(legendDataForTimeAtCursor.odFanPower) || '--'}</S.AnscillaryData>
              </S.AnscillaryContainer>
              <S.AnscillaryContainer>
                <S.AnscillaryLabel>OD Compressor Power </S.AnscillaryLabel>
                <S.AnscillaryData>{watts(legendDataForTimeAtCursor.odCompressorPower) || '--'}</S.AnscillaryData>
              </S.AnscillaryContainer>
              <S.AnscillaryContainer>
                <S.AnscillaryLabel>OD Line Voltage</S.AnscillaryLabel>
                <S.AnscillaryData>{voltageAC(legendDataForTimeAtCursor.odLineVoltage) || '--'}</S.AnscillaryData>
              </S.AnscillaryContainer>
              <S.AnscillaryContainer>
                <S.AnscillaryLabel>OD AC Input Current</S.AnscillaryLabel>
                <S.AnscillaryData>{amps(legendDataForTimeAtCursor.odInputCurrent) || '--'}</S.AnscillaryData>
              </S.AnscillaryContainer>
            </>
          )}
          <S.AnscillaryContainer>
            <S.AnscillaryLabel>Compressor Discharge Temp</S.AnscillaryLabel>
            <S.AnscillaryData>{degreesFahrenheit(legendDataForTimeAtCursor.odCompressorDischargeTemperature) || '--'}</S.AnscillaryData>
          </S.AnscillaryContainer>
        </>
      }
    </S.LegendGroup>
  )
}

export default LegendGroup

const S = {
  AnscillaryContainer: styled.div`
    display: grid;
    grid-template-columns: 60% 40%;
  `,
  AnscillaryLabel: styled.label`
    font-weight: bold;
    font-size: 12px;
    margin-left: 45px
  `,
  AnscillaryData: styled.label`
    display: inline;
    white-space: nowrap;
    margin: auto;

    & > :nth-child(2):before {
      content: '(';
      margin-left: 1.5px;
    }

    & > :nth-child(2):after {
      content: ')';
    }
  `,
  LegendGroup: styled.div`
    min-width: 280px;
    padding: 5px;
    margin: 5px;
  `
}
